import { useQuery } from "@tanstack/react-query";
import type { PersonPto } from "@/lib/person-leave";
import type { PtoReport } from "@/lib/pto";
import { ptoKeys } from "@/lib/query";

/**
 * Company-wide leave for a date range, proxied from the Nymbl API Gateway by
 * `GET /api/pto` (admin only).
 *
 * A live read of NOS rather than a cached copy in our database, so it gets a
 * long `staleTime` — a year of leave is ~1,500 rows over six gateway pages, and
 * refetching on window focus would spend that for nothing. Every view on the
 * report is a client-side rollup of this one payload.
 */
export function usePtoReport(from: string, to: string) {
  return useQuery({
    queryKey: ptoKeys.range(from, to),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<PtoReport> => {
      const res = await fetch(`/api/pto?from=${from}&to=${to}`);
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        const message =
          body !== null && typeof body === "object" && "error" in body
            ? String(body.error)
            : "Could not load the PTO report";
        throw new Error(message);
      }
      const json = await res.json();
      return json.data;
    },
  });
}

/**
 * One person's upcoming leave — approved and awaiting approval — from
 * `GET /api/persons/:personId/pto`.
 *
 * Restricted to the person, their direct manager and admins; a viewer outside
 * that gets a 403, so `enabled` keeps the request out of the way entirely when
 * the profile already knows the tab is not for them.
 *
 * A live NOS read like the company report, so it takes the same long
 * `staleTime` rather than refetching on every focus.
 */
export function usePersonPto(personId: string, enabled: boolean) {
  return useQuery({
    queryKey: ptoKeys.person(personId),
    enabled: enabled && personId !== "",
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<PersonPto> => {
      const res = await fetch(`/api/persons/${encodeURIComponent(personId)}/pto`);
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        const message =
          body !== null && typeof body === "object" && "error" in body
            ? String(body.error)
            : "Could not load this person's leave";
        throw new Error(message);
      }
      const json = await res.json();
      return json.data;
    },
  });
}
